<template>
  <div>
    <b-modal
      id="compare-cases-modal"
      ref="compare-cases-modal"
      centered
      :ok-title="$t('DEBTIA_MERGE')"
      cancel-variant="outline-secondary"
      size="md"
      no-close-on-backdrop
      @ok="mergeCase"
      @cancel="cancelModal"
      @close="cancelModal"
    >
      <b-overlay
        id="overlay-background"
        :show="modelLoader"
        variant="dark"
        opacity="0.85"
        blur="2px"
        rounded="sm"
        spinner-variant="primary"
        spinner-medium
      >
        <b-alert
          v-if="errorMsg"
          variant="danger"
          show
        >
          <div class="alert-body">
            <span>{{ errorMsg }}</span>
          </div>
        </b-alert>
        <div>
          <b-row>
            <b-col
              offset="1"
              cols="10"
            >
              <b-form-group
                :label="$t('DEBTIA_MAIN_CASE')"
                label-for="case-login-status"
              >
                <v-select
                  v-model="selectedMainCase"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="filteredCaseOptions"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              cols="12"
              class="d-flex justify-content-around mt-2 mb-2"
            >
              <b-table
                ref="roles"
                class="position-relative"
                :fields="tableFields"
                :items="localMergeCaseItems"
                responsive
                show-empty
                empty-text="No matching records found"
              >
                <template #empty="">
                  <p class="text-center">
                    {{ $t('DEBTIA_NO_RECORD_FOUND') }}
                  </p>
                </template>
                <template #cell(case_id)="data">
                  {{ data.item.case }}
                </template>
                <template #cell(debtor_id)="data">
                  {{ data.item.main_debtor }} <span style="color: gold">&#9733;</span><br>
                  <ul style="list-style-type: none; padding: 0; margin: 0">
                    <li
                      v-for="(debtor, index) in data.item.debtors"
                      :key="index"
                    >
                      {{ debtor }}
                    </li>
                  </ul>
                </template>
                <template #cell(action)="data">
                  <feather-icon
                    icon="XIcon"
                    size="16"
                    style="color: red; cursor: pointer;"
                    @click="deleteItem(data.index, Object.keys(data.item))"
                  />
                </template>
              </b-table>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-modal>
    <merge-cases
      :compare-data="compareData"
      :user-data="userData"
      :selected-main-case="selectedMainCase"
      :selected-cases="selectedCases"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BOverlay, BFormGroup, BTable, BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import constants from '@/app-configs/constants'
import i18n from '@/libs/i18n'
import { EventBus } from '@/event-bus'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import MergeCases from './MergeCases.vue'

export default {
  components: {
    BRow,
    BCol,
    BOverlay,
    BFormGroup,
    vSelect,
    MergeCases,
    BTable,
    BAlert,
  },
  directives: {
    Ripple,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    selectedCases: {
      type: Array,
      required: true,
    },
    mergeCasesItems: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      modelLoader: false,
      errorMsg: '',
      selectedMainCase: {},
      caseOptions: [],
      skip_debtor_merge: false,
      mergeData: [],
      compareData: [],
      localMergeCaseItems: [],
    }
  },
  computed: {
    filteredCaseOptions() {
      return this.caseOptions.filter(option => this.localMergeCaseItems.map(item => item.case_id).includes(option.value))
        .sort((a, b) => a.value - b.value)
    },
    tableFields() {
      const temp = [
        { key: 'case_id', sortable: false, label: this.$t('DEBTIA_CASE') },
        { key: 'debtor_id', sortable: false, label: this.$t('DEBTIA_CLIENT_DEBTORS') },
        { key: 'action', sortable: false, label: this.$t('DEBTIA_ACTION') },
      ]
      return temp
    },
  },
  watch: {
    mergeCasesItems: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.modelLoader = true
        setTimeout(() => {
          this.modelLoader = false
          this.localMergeCaseItems = Object.values(newValue)
        }, 1000)
      },
    },
    selectedMainCase: {
      handler(newValue) {
        if (newValue.value) {
          this.errorMsg = ''
        }
      },
    },
  },
  created() {
    this.fetchCases(this.userData.data.id)
  },
  mounted() {
    this.localMergeCaseItems = [...Object.values(this.mergeCasesItems)]
    if (this.localMergeCaseItems.length <= 0) {
      this.modelLoader = true
      setTimeout(() => {
        this.modelLoader = false
      }, 1000)
    }
  },
  methods: {
    async fetchCases(id) {
      this.modelLoader = true
      const url = `${constants.baseUrl}/debt-cases/dropdown?client_id=${id}`
      await axios.get(url)
        .then(response => {
          this.modelLoader = false
          if (response.status === 200) {
            this.caseOptions = response.data.data
          }
        })
    },
    cancelModal() {
      if (this.errorMsg) {
        this.errorMsg = ''
      }
      this.selectedMainCase = {}
      this.localMergeCaseItems = []
      this.$refs['compare-cases-modal'].hide()
    },
    mergeCase(event) {
      event.preventDefault()
      this.modelLoader = true

      if (!this.selectedMainCase.value) {
        this.modelLoader = false
        this.errorMsg = i18n.t('DEBTIA_SELECT_MAIN_CASE')
        return
      }
      const url = `${constants.baseUrl}/debt-cases/debtors/compare`
      const cases = this.localMergeCaseItems.map(item => item.case_id)
      axios.post(url, {
        main_case_id: this.selectedMainCase.value || null,
        creditor_id: this.userData.data.id || null,
        case_ids: cases || null,
      }).then(response => {
        if (response.status === 200) {
          if (response.data.data.compare.length === 0) {
            let caseIds = []
            caseIds = response.data.data.case_ids.filter(id => id !== this.selectedMainCase.value)
            const mergeUrl = `${constants.baseUrl}/debt-cases/merge`
            axios.post(mergeUrl, {
              main_case_id: this.selectedMainCase.value || null,
              creditor_id: this.userData.data.id || null,
              case_ids: caseIds || null,
              skip_debtor_merge: true,
            }).then(response1 => {
              if (response1.status === 200) {
                this.modelLoader = false
                this.selectedMainCase = {}
                this.localMergeCaseItems = []
                EventBus.$emit('client-updated')
                this.$refs['compare-cases-modal'].hide()
                EventBus.$emit('merge-success')
                this.$swal({
                  icon: 'success',
                  title: i18n.t('DEBTIA_CASE_MERGED'),
                  text: i18n.t('DEBTIA_MERGE_SUCCESS'),
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
              }
            }).catch(error => {
              this.modelLoader = false
              const [fistMessage] = error.response.data.message
              this.errorMsg = fistMessage
            })
          } else {
            this.modelLoader = false
            this.compareData = response.data.data
            this.$refs['compare-cases-modal'].hide()
            this.$nextTick(() => {
              this.$root.$emit('bv::show::modal', 'merge-cases-modal')
            })
          }
        }
      }).catch(error => {
        this.modelLoader = false
        const [fistMessage] = error.response.data.message
        this.errorMsg = fistMessage
      })
    },
    deleteItem(index) {
      const itemToDelete = this.localMergeCaseItems[index]
      const caseId = itemToDelete.case_id

      if (caseId === this.selectedMainCase.value) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('Error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: i18n.t('DEBTIA_MAIN_CASE_ERROR_MSG'),
          },
        })
        return
      }
      if (this.localMergeCaseItems.length <= 2) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('Error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: i18n.t('DEBTIA_MERGE_LIST_ERROR_MSG'),
          },
        })
      }

      if (this.canDeleteCase(this.localMergeCaseItems, caseId)) {
        const updatedItems = [...this.localMergeCaseItems]
        updatedItems.splice(index, 1)

        this.localMergeCaseItems = updatedItems
        this.$emit('update:mergeCaseItems', this.localMergeCaseItems)
      }
    },

    canDeleteCase(items, caseId) {
      const caseIds = new Set(items.map(item => item.case_id))
      const mainCaseId = this.selectedMainCase.value

      if (caseId === mainCaseId) {
        return false
      }

      if (caseIds.size <= 2) {
        return false
      }

      return true
    },

  },
}
</script>
